import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Material UI
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';

// Clsx
import clsx from 'clsx';

// Types
import { ApplicationState } from 'store';
import { XlsHeaderInterface, XlsRowInterface } from 'components';

// Helpers
import { currencyFormat } from 'helpers/currencyFormat';
import { Capitalize } from 'helpers/capitalize';

// Constants
import CONST_EMPLOYEES from 'constants/employees';

// Styles
import useStyles from './tableOperatorCommission.styles';

const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);

interface TableOperatorCommissionInterface {
  setTableCSV: (arg: any) => void;
  periodString?: string;
  setSimplePdfData?: (param: {
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }) => void;
  setXlsHeader?: (param: XlsHeaderInterface[] | undefined) => void;
  setXlsRows?: (param: Array<XlsRowInterface[]>) => void;
}

type Props = TableOperatorCommissionInterface;

const TableOperatorCommission: React.FC<Props> = ({
  setTableCSV,
  periodString,
  setSimplePdfData,
  setXlsHeader,
  setXlsRows,
}) => {
  const classes = useStyles();

  const [employeesHeader, setEmployeesHeader] = useState<{
    [key: string]: number;
  }>({});

  const employeesReport = useSelector(
    (state: ApplicationState) => state.employees.employeesReports || null,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const isValid = useMemo(
    () => (value: number | string): boolean =>
      value !== 'NaN' && !Number.isNaN(value),
    [],
  );

  useEffect(() => {
    if (Object.values(employeesReport).length > 0) {
      let biggest = {};
      Object.entries(employeesReport).forEach(([key, value]) => {
        if (
          Object.values(value).length > Object.values(biggest).length &&
          key
        ) {
          biggest = value;
        }
      });
      setEmployeesHeader(biggest);
    }
  }, [employeesReport]);

  // generating the table csv
  useEffect(() => {
    let csv = 'name;';
    if (employeesReport) {
      // Handle header

      const columns = Object.keys(employeesHeader)?.filter(
        (key) => key !== 'transportOperatorId',
      );
      csv += columns.join(';');
      csv += '\n';

      // Handle data
      Object.entries(employeesReport).forEach(([key, data]: any) => {
        csv += `${key};`;
        const values = columns.map((column: any) => {
          const value = data[column];
          if (!Number.isNaN(Number(value)) && key !== 'transportOperatorId') {
            return currencyFormat({
              value: isValid(value) ? Number(value) : 0,
            });
          }

          return ' ';
        });
        csv += values.join(';');
        csv += '\n';
      });

      setTableCSV(csv);
    }
  }, [employeesReport, setTableCSV, employeesHeader]);

  useEffect(() => {
    if (employeesReport) {
      const firstColumnHeader = ['name'];

      const dynamicColumnHeader = Object.keys(employeesHeader)
        ?.filter((key) => key !== 'transportOperatorId')
        .map((key) => CONST_EMPLOYEES.TABLE_HEADER.get(key) || key);

      const headerSimplePdf = firstColumnHeader.concat(dynamicColumnHeader);
      const headerKeys = Object.keys(employeesHeader);

      const rowsSimplePdf = Object.entries(employeesReport).map(
        ([key, values]) => {
          const row = headerKeys
            .filter((key) => key !== 'transportOperatorId')
            .map((item) => {
              if (!Number.isNaN(Number(values[item as keyof typeof values]))) {
                return currencyFormat({
                  value: isValid(values[item as keyof typeof values]!)
                    ? Number(values[item as keyof typeof values])
                    : 0,
                });
              }
              return ' ';
            });
          return [Capitalize(key), ...row];
        },
      );

      const tempSimplePdfData = {
        title: 'ONBOARD',
        subtitle: 'Relatório de comissão de operadores',
        filters: periodString || '',
        headers: headerSimplePdf,
        rows: rowsSimplePdf,
      };
      if (setSimplePdfData) setSimplePdfData(tempSimplePdfData);
    }
  }, [employeesReport, employeesHeader]);

  useEffect(() => {
    if (employeesReport) {
      const staticXlsheader = [
        {
          header: 'Nome',
          key: 'name',
          width: 20,
        },
        ...Object.keys(employeesHeader)
          .filter((key) => key !== 'transportOperatorId')
          ?.map((key) => ({
            header: `${CONST_EMPLOYEES.TABLE_HEADER.get(key) || key}`,
            key,
            width: 20,
          })),
      ];

      if (transportOperatorSelected.type === 1) {
        staticXlsheader.push({
          header: 'Operação',
          key: 'operator',
          width: 20,
        });
      }

      if (setXlsHeader) setXlsHeader(staticXlsheader);

      const rows = Object.entries(employeesReport).map(([key, values], i) => {
        const row = [
          { key: 'name', value: `${key}`, image: null },
          ...Object.entries(values)
            .filter(
              ([key, value]) =>
                !Number.isNaN(Number(value)) && key !== 'transportOperatorId',
            )
            .map(([key, value]) => ({
              key,
              value: `${currencyFormat({
                value: isValid(value) ? Number(value) : 0,
              })}`,
              image: null,
            })),
        ];

        if (transportOperatorSelected.type === 1) {
          row.push({
            key: 'operator',
            value: `${transportOperatorsList.find(
              (e) => e.id === values?.transportOperatorId,
            )?.name || '---'}`,
            image: null,
          });
        }
        return row;
      }) as XlsRowInterface[][];

      if (setXlsRows) setXlsRows(rows);
    }
  }, [employeesHeader]);

  return (
    <Grid component='div'>
      {Object.entries(employeesReport)?.length > 1 && (
        <Box width='100%' display='flex' justifyContent='flex-end'>
          <Suspense fallback='Carregando...'>
            <TotalCountComponent
              count={Object.entries(employeesReport)?.length - 1 || 0}
              title='usuários'
              position='right'
            />
          </Suspense>
        </Box>
      )}

      <TableContainer className={classes.tableContainer}>
        <Table
          className={`${classes.table} MuiDialogContent-root`}
          aria-label='collapsible table'>
          <TableHead>
            <TableRow className={classes.theadBg}>
              <TableCell variant='head' align='left'>
                Nome
              </TableCell>
              {Object.keys(employeesHeader)?.map((key) =>
                key !== 'transportOperatorId' ? (
                  <TableCell key={key} variant='head' align='left'>
                    {CONST_EMPLOYEES.TABLE_HEADER.get(key) || key}
                  </TableCell>
                ) : (
                  <></>
                ),
              )}
              {transportOperatorSelected.type === 1 && (
                <TableCell
                  key='transportOperatorSelected'
                  variant='head'
                  align='left'>
                  Operação
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {employeesReport &&
              Object.entries(employeesReport).map(([key, values], index) => {
                const headerKeys = Object.keys(employeesHeader);

                return (
                  <TableRow
                    key={`row-${index}`}
                    className={
                      index % 2 === 0 ? classes.evenRow : classes.oddRow
                    }>
                    <TableCell
                      className={clsx(classes.head, {
                        [classes.headZero]: index === 0,
                      })}>
                      {Capitalize(key)}
                    </TableCell>
                    {headerKeys.map((item, cellIndex) => {
                      if (
                        !Number.isNaN(
                          Number(values[item as keyof typeof values]),
                        ) &&
                        item !== 'transportOperatorId'
                      ) {
                        return (
                          <TableCell key={`${key}-${cellIndex}`}>
                            {currencyFormat({
                              value: isValid(
                                values[item as keyof typeof values]!,
                              )
                                ? Number(values[item as keyof typeof values])
                                : 0,
                            })}
                          </TableCell>
                        );
                      }
                      return item !== 'transportOperatorId' ? (
                        <TableCell key={`${key}-${cellIndex}-empty`} />
                      ) : (
                        <> </>
                      );
                    })}
                    {transportOperatorSelected.type === 1 && (
                      <TableCell key={`${key}-operator`}>
                        {transportOperatorsList.find(
                          (e) => e.id === values?.transportOperatorId,
                        )?.name || '---'}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            {/* Object.values(employeesHeader).length >
                    Object.values(values).length &&
                    Array.from(
                      Array(
                        Object.values(employeesHeader)?.length -
                          Object.values(values)?.length,
                      ).keys(),
                    ).map((e) => <TableCell key={e} />) */}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export { TableOperatorCommission };
export default TableOperatorCommission;
